import React, { useState, useRef, useEffect } from 'react';
import { BrowserMultiFormatReader, DecodeHintType, BarcodeFormat } from '@zxing/library';
import { Icon } from './icons';
import { Sheet, ImgCapa } from './common';
import { unitMeta, UNIT_OPTIONS } from './units';
import { isAndroidNativeApp } from './platform';
import { tlog } from './telemetry';

type Mode = 'choose' | 'scan' | 'manual' | 'preview' | 'search';

interface BookSearchResult {
  title: string;
  author: string;
  pages: number | null;
  coverSourceUrl: string | null;
  isbn: string | null;
  publishedDate: string | null;
}

interface ISBNResult {
  isbn: string;
  title: string;
  author: string;
  pages: number | null;
  coverSourceUrl: string | null;
  publisher: string | null;
  publishedDate: string | null;
  description: string | null;
  source: string;
}

export const AddBookSheet: React.FC<{
  open: boolean;
  onClose: () => void;
  onAdd: (b: {
    title: string; author: string; totalPages: number; dailyGoal: number;
    targetDate?: string | null; note?: string;
    coverSourceUrl?: string | null; isbn?: string | null;
    progressUnit?: 'pages' | 'chapters' | 'positions';
    status?: 'reading' | 'wishlist' | 'finished';
    genre?: string | null; tags?: string[]; shelfIds?: string[];
  }) => Promise<void>;
  onLookupISBN?: (isbn: string) => Promise<ISBNResult>;
  onSearchBooks?: (q: string) => Promise<{ results: BookSearchResult[] }>;
  onUpgrade?: () => void;
  showStatus?: boolean; // false no add de clube (status é decidido pelo backend do clube)
  shelves?: any[];      // FP3 — estantes do usuário, pra escolher já na criação
}> = ({ open, onClose, onAdd, onLookupISBN, onSearchBooks, onUpgrade, showStatus = true, shelves = [] }) => {
  const [mode, setMode] = useState<Mode>('choose');
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState<BookSearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  // A busca falhou de verdade? E diferente de nao ter achado nada.
  const [searchErro, setSearchErro] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: '', author: '', pages: '', dailyGoal: '20', unit: 'pages', targetDate: '', note: '',
    coverSourceUrl: '' as string | null, isbn: '' as string | null,
    status: 'reading' as 'reading' | 'wishlist' | 'finished',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // FP3 — gênero, tags e estante já na criação
  const [genre, setGenre] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [shelfIds, setShelfIds] = useState<string[]>([]);
  const GENRES = ['Romance', 'Ficção', 'Fantasia', 'Suspense', 'Terror', 'Autoajuda', 'Negócios', 'História', 'Biografia', 'Filosofia', 'Poesia', 'HQ/Mangá', 'Técnico', 'Religião', 'Infantojuvenil'];
  const addTag = () => { const t = tagInput.trim(); if (t && !tags.includes(t) && tags.length < 20) { setTags([...tags, t]); setTagInput(''); } };
  const toggleShelfSel = (id: string) => setShelfIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const [scanBusy, setScanBusy] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [focusing, setFocusing] = useState<{ x: number; y: number } | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const trackRef = useRef<MediaStreamTrack | null>(null);

  const reset = () => {
    setForm({ title: '', author: '', pages: '', dailyGoal: '20', unit: 'pages', targetDate: '', note: '', coverSourceUrl: null, isbn: null, status: 'reading' });
    setGenre(''); setTags([]); setTagInput(''); setShelfIds([]);
    setMode('choose');
    setError(null);
    setScanError(null);
    setSearchQ(''); setSearchResults(null); setSearching(false);
  };

  // O Sheet não desmonta (só some via CSS), e fechar por navegação (back) chama
  // setAddOpen(false) sem passar pelo handleClose — então reseta aqui quando
  // fecha, pra não reabrir no modo 'search' com a busca/form antigos.
  useEffect(() => { if (!open) reset(); }, [open]); // eslint-disable-line

  // Busca de livro por NOME (debounce 350ms) — só ativa no modo 'search'.
  useEffect(() => {
    if (mode !== 'search') return;
    const term = searchQ.trim();
    if (term.length < 2) { setSearchResults(null); setSearching(false); return; }
    if (!onSearchBooks) return;
    let alive = true;
    setSearching(true);
    setSearchErro(null);
    const t = setTimeout(() => {
      onSearchBooks(term)
        .then(r => { if (alive) { setSearchResults(r?.results || []); setSearching(false); } })
        // ERRO DE REDE NAO E "NAO ACHEI".
        //
        // O catch zerava os resultados, e a tela so olhava o tamanho da lista:
        // sessao vencida depois de horas em segundo plano, ou um elevador, e a
        // pessoa lia "Nao achei esse livro" e ia cadastrar na mao — perdendo
        // capa, autor e paginas que a busca traria.
        .catch((e) => { if (alive) { setSearchResults([]); setSearchErro(String(e?.message || 'Nao consegui buscar agora.')); setSearching(false); } });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [searchQ, mode, onSearchBooks]);

  // Escolheu um livro da busca → pré-preenche e vai pro preview (ajustável lá).
  const pickSearchResult = (b: BookSearchResult) => {
    setForm(prev => ({
      title: b.title || '',
      author: b.author || '',
      pages: String(b.pages || ''),
      dailyGoal: '20',
      unit: prev.unit || 'pages',
      targetDate: '',
      note: '',
      coverSourceUrl: b.coverSourceUrl || null,
      isbn: b.isbn || null,
      status: prev.status,
    }));
    setMode('preview');
  };

  // Seletor de status no adicionar: Ler agora (conta no cap), Ler depois e Já li
  // (não contam — estante ilimitada). Usado no manual e no preview.
  const statusPicker = (
    <div className="field">
      <label>Como adicionar?</label>
      <div style={{ display: 'flex', gap: 6 }}>
        {([
          { v: 'reading', l: 'Ler agora' },
          { v: 'wishlist', l: 'Ler depois' },
          { v: 'finished', l: 'Já li' },
        ] as const).map(o => (
          <button key={o.v} type="button" onClick={() => setForm(f => ({ ...f, status: o.v }))} style={{
            flex: 1, padding: '10px 6px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            background: form.status === o.v ? 'var(--ember-soft)' : 'var(--paper-2)',
            color: form.status === o.v ? 'var(--ember-2)' : 'var(--ink-3)',
            border: '1px solid ' + (form.status === o.v ? 'var(--ember)' : 'var(--line)'),
          }}>{o.l}</button>
        ))}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginTop: 6, lineHeight: 1.35 }}>Sem assinatura, dá para ler 2 ao mesmo tempo. "Ler depois" e "Já li" não contam — a estante é ilimitada.</div>
    </div>
  );

  // FP3 — gênero, tags e estante (mesmo bloco no manual e no preview/busca)
  const organizeBlock = (
    <>
      <div className="field">
        <label>Gênero</label>
        <input value={genre} onChange={e => setGenre(e.target.value)} placeholder="Ex: Romance, Ficção…" maxLength={60} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
          {GENRES.map(g => (
            <button key={g} type="button" onClick={() => setGenre(genre === g ? '' : g)} style={{ padding: '5px 10px', borderRadius: 999, fontSize: 11.5, cursor: 'pointer', background: genre === g ? 'var(--ember-soft)' : 'var(--paper-2)', color: genre === g ? 'var(--ember-2)' : 'var(--ink-3)', border: '1px solid ' + (genre === g ? 'var(--ember)' : 'var(--line)') }}>{g}</button>
          ))}
        </div>
      </div>
      <div className="field">
        <label>Tags</label>
        {tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 6 }}>
            {tags.map(t => (
              <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 6px 5px 10px', borderRadius: 999, fontSize: 12, background: 'var(--ember-soft)', color: 'var(--ember-2)' }}>{t}<button type="button" onClick={() => setTags(tags.filter(x => x !== t))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ember-2)', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button></span>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6 }}>
          <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} placeholder="Adicionar tag…" maxLength={40} style={{ flex: 1 }} />
          <button type="button" onClick={addTag} disabled={!tagInput.trim()} style={{ padding: '0 16px', borderRadius: 12, border: '1px solid var(--line)', background: 'var(--paper-2)', color: 'var(--ink-2)', fontSize: 18, cursor: 'pointer' }}>+</button>
        </div>
      </div>
      <div className="field">
        <label>Estante</label>
        {shelves.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--ink-4)', lineHeight: 1.4 }}>Sem estantes criadas — o livro fica na biblioteca padrão. Crie estantes na aba Biblioteca.</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {shelves.map((s: any) => {
              const on = shelfIds.includes(s.id);
              return <button key={s.id} type="button" onClick={() => toggleShelfSel(s.id)} style={{ padding: '7px 12px', borderRadius: 999, fontSize: 12.5, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, background: on ? 'var(--ember-soft)' : 'var(--paper-2)', color: on ? 'var(--ember-2)' : 'var(--ink-3)', border: '1px solid ' + (on ? 'var(--ember)' : 'var(--line)') }}>{on && <Icon name="check" size={11} color="var(--ember-2)" stroke={3} />}{s.emoji ? s.emoji + ' ' : ''}{s.name}</button>;
            })}
          </div>
        )}
      </div>
    </>
  );

  // Cleanup do scanner quando sheet fecha ou muda de modo
  useEffect(() => {
    if (!open || mode !== 'scan') {
      stopScanner();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);
  useEffect(() => () => { stopScanner(); }, []);  // eslint-disable-line

  // ─────────────────────────────────────────────────────────────────────
  // Android nativo: usa plugin @capacitor-mlkit/barcode-scanning (MLKit
  // nativo, UI full-screen). zxing + getUserMedia + <video> não funciona
  // de forma confiável em Capacitor Android WebView (autoplay de MediaStream
  // é bloqueado, daí o user via o placeholder de play do Chromium em vez
  // da câmera). iOS e web continuam usando zxing — funciona perfeito lá.
  // ─────────────────────────────────────────────────────────────────────
  const startScannerAndroidNative = async () => {
    setScanError(null);
    setScanBusy(true);
    tlog('scan:android_start');
    try {
      const mod: any = await import('@capacitor-mlkit/barcode-scanning');
      const BarcodeScanner = mod.BarcodeScanner;
      const NativeFormat = mod.BarcodeFormat;

      // Garante módulo MLKit baixado (download on-demand via Google Play
      // Services). Primeira vez pode demorar alguns segundos.
      try {
        const status = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
        if (!status?.available) {
          await BarcodeScanner.installGoogleBarcodeScannerModule();
        }
      } catch { /* alguns devices não expõem o check — segue tentando scan */ }

      // Permissão runtime de câmera
      let perm = await BarcodeScanner.checkPermissions();
      if (perm.camera !== 'granted') {
        perm = await BarcodeScanner.requestPermissions();
      }
      if (perm.camera !== 'granted') {
        setScanError('Permissão de câmera negada. Habilite nas configurações do app ou digite o ISBN manualmente.');
        setScanBusy(false);
        return;
      }

      setScanBusy(false);

      // Abre UI nativa full-screen — bloqueia até user escanear ou cancelar
      const result = await BarcodeScanner.scan({
        formats: [
          NativeFormat.Ean13, NativeFormat.Ean8,
          NativeFormat.UpcA, NativeFormat.UpcE,
          NativeFormat.Code128,
        ],
      });

      const first = result?.barcodes?.[0];
      if (!first?.rawValue) {
        tlog('scan:android_cancelled');
        // User cancelou — fica no choose silenciosamente
        return;
      }

      const code = first.rawValue.replace(/[^0-9Xx]/g, '');
      if (code.length !== 10 && code.length !== 13) {
        setScanError('Código lido não é um ISBN válido. Tente outro ou digite manualmente.');
        return;
      }

      tlog('scan:android_hit', { isbnLen: code.length });
      await handleISBN(code);
    } catch (e: any) {
      tlog('scan:android_exception', { msg: e?.message?.slice(0, 200) });
      setScanBusy(false);
      setScanError(e?.message || 'Erro ao escanear');
    }
  };

  const startScanner = async () => {
    setScanError(null); setScanBusy(true); setTorchOn(false); setTorchSupported(false);
    tlog('scan:web_start');
    try {
      const video = videoRef.current;
      if (!video) throw new Error('Vídeo não disponível');

      // Constraints: câmera traseira, alta resolução, autofoco contínuo
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          advanced: [
            // @ts-ignore — focusMode não está no lib DOM types mas é suportado no navegador
            { focusMode: 'continuous' },
          ] as any,
        },
        audio: false,
      };

      const hints = new Map();
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.EAN_13, BarcodeFormat.EAN_8,
        BarcodeFormat.UPC_A, BarcodeFormat.UPC_E,
        BarcodeFormat.CODE_128,
      ]);
      const reader = new BrowserMultiFormatReader(hints);
      readerRef.current = reader;

      // decodeFromConstraints faz getUserMedia + attach + callback contínuo
      reader.decodeFromConstraints(constraints, video, async (result) => {
        // Captura o stream/track na primeira chance (fica dentro de video.srcObject)
        if (!streamRef.current && video.srcObject instanceof MediaStream) {
          streamRef.current = video.srcObject;
          const t = video.srcObject.getVideoTracks()[0];
          trackRef.current = t;
          try {
            const caps: any = t?.getCapabilities?.() || {};
            if (caps.torch) setTorchSupported(true);
          } catch {}
        }
        if (result) {
          const code = result.getText().replace(/[^0-9Xx]/g, '');
          if (code.length === 10 || code.length === 13) {
            stopScanner();
            await handleISBN(code);
          }
        }
      });
    } catch (e: any) {
      setScanError(e?.message || 'Não foi possível acessar a câmera');
      stopScanner();
    } finally {
      setScanBusy(false);
    }
  };

  function stopScanner() {
    try { readerRef.current?.reset(); } catch {}
    readerRef.current = null;
    try {
      streamRef.current?.getTracks().forEach(t => t.stop());
    } catch {}
    streamRef.current = null;
    trackRef.current = null;
    setTorchOn(false);
    setFocusing(null);
  }

  const handleVideoTap = async (e: React.MouseEvent<HTMLVideoElement> | React.TouchEvent<HTMLVideoElement>) => {
    const track = trackRef.current;
    if (!track) return;
    const video = videoRef.current;
    if (!video) return;
    const rect = video.getBoundingClientRect();
    const anyE = e as any;
    const cx = (anyE.touches?.[0]?.clientX ?? anyE.clientX) - rect.left;
    const cy = (anyE.touches?.[0]?.clientY ?? anyE.clientY) - rect.top;
    const x = Math.max(0, Math.min(1, cx / rect.width));
    const y = Math.max(0, Math.min(1, cy / rect.height));
    setFocusing({ x: cx, y: cy });
    setTimeout(() => setFocusing(null), 700);
    try {
      const caps: any = track.getCapabilities?.() || {};
      const advanced: any[] = [];
      if (caps.pointsOfInterest) advanced.push({ pointsOfInterest: [{ x, y }] });
      if (caps.focusMode && caps.focusMode.includes('single-shot')) advanced.push({ focusMode: 'single-shot' });
      else if (caps.focusMode && caps.focusMode.includes('continuous')) advanced.push({ focusMode: 'continuous' });
      if (advanced.length) await track.applyConstraints({ advanced } as any);
    } catch (err) {
      // silencioso — nem todos os devices suportam
    }
  };

  const toggleTorch = async () => {
    const track = trackRef.current;
    if (!track) return;
    try {
      const next = !torchOn;
      await track.applyConstraints({ advanced: [{ torch: next } as any] } as any);
      setTorchOn(next);
    } catch {
      setScanError('Lanterna não disponível neste dispositivo.');
    }
  };

  const handleISBN = async (isbn: string) => {
    tlog('isbn:lookup_start', { len: isbn.length });
    if (!onLookupISBN) {
      setForm(f => ({ ...f, isbn }));
      setMode('manual');
      return;
    }
    setScanBusy(true); setScanError(null);
    try {
      const data = await onLookupISBN(isbn);
      setForm(prev => ({
        title: data.title || '',
        author: data.author || '',
        pages: String(data.pages || ''),
        dailyGoal: '20',
        unit: prev.unit || 'pages',   // preserva escolha anterior; ajustável no preview
        targetDate: '',
        note: '',
        coverSourceUrl: data.coverSourceUrl || null,
        isbn: data.isbn,
        status: prev.status,
      }));
      tlog('isbn:lookup_ok', { title: (data.title || '').slice(0, 60), source: (data as any).source });
      setMode('preview');
    } catch (e: any) {
      tlog('isbn:lookup_err', { msg: (e?.message || '').slice(0, 160) });
      setScanError(e?.message || 'ISBN não encontrado. Você pode preencher manualmente.');
      setTimeout(() => {
        setForm(f => ({ ...f, isbn }));
        setMode('manual');
      }, 1500);
    } finally {
      setScanBusy(false);
    }
  };

  const handleAdd = async () => {
    // Total (páginas/capítulos/posições) é OBRIGATÓRIO: sem ele o progresso, a
    // meta e a chama ficam errados. Não inventamos mais 200 em silêncio.
    if (!form.title || !(+form.pages > 0) || saving) return;
    setSaving(true); setError(null);
    try {
      await onAdd({
        title: form.title,
        author: form.author || 'Autor desconhecido',
        totalPages: +form.pages || 200,
        dailyGoal: +form.dailyGoal || 20,
        progressUnit: (form.unit as any) || 'pages',
        targetDate: form.targetDate || null,
        note: form.note || '',
        coverSourceUrl: form.coverSourceUrl || null,
        isbn: form.isbn || null,
        status: form.status,
        genre: genre.trim() || null,
        tags,
        shelfIds,
      });
      reset();
      onClose();
    } catch (e: any) {
      const msg = (e?.message || '') + '';
      // Só o status 'Ler agora' (reading) bate no cap de leituras simultâneas. A
      // estante é ilimitada — então em vez de "pagar", guiamos pra "Ler depois".
      if (msg.includes('plan_limit') || msg.includes('plan_required')) {
        // Caminho quase morto (pente fino, DIR-01): sem assinatura e sem teste
        // o app abre no paywall obrigatório. Só chega aqui quem perdeu o
        // acesso com o app aberto. O plano "Free" não é vendido: não citar.
        setError('Sem assinatura, dá para ler 2 livros ao mesmo tempo. Adicione este como "Ler depois" e comece quando quiser — ou pause ou termine um dos atuais. A estante é ilimitada.');
      } else {
        setError(msg || 'Erro ao criar');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    stopScanner();
    reset();
    onClose();
  };

  return (
    <Sheet open={open} onClose={handleClose}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
            Novo hábito
          </div>
          <div className="serif" style={{ fontSize: 30, lineHeight: 1.05 }}>
            {mode === 'scan' ? 'Escaneando' : mode === 'preview' ? 'Encontrei' : 'Adicionar livro'}
          </div>
        </div>
        <button onClick={handleClose} style={{
          width: 32, height: 32, borderRadius: '50%',
          background: 'var(--paper-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}><Icon name="close" size={16}/></button>
      </div>

      {mode === 'choose' && (
        <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {onSearchBooks && (
          <button
            onClick={() => { setSearchQ(''); setSearchResults(null); setMode('search'); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: 18, borderRadius: 18,
              background: 'linear-gradient(135deg, var(--ember-soft), var(--paper-2))',
              border: '1px solid var(--line)',
              textAlign: 'left',
            }}
          >
            <div style={{ width: 48, height: 48, borderRadius: 14, background: 'var(--ember)', color: 'var(--paper)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="search" size={24} color="var(--paper)" stroke={2}/>
            </div>
            <div style={{ flex: 1 }}>
              <div className="serif" style={{ fontSize: 20, lineHeight: 1.1 }}>Buscar pelo nome</div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>Digite o título e escolha — capa e páginas vêm prontas.</div>
            </div>
            <Icon name="chevron-right" size={16} color="var(--ink-3)"/>
          </button>
          )}
          <button
            onClick={async () => {
              if (isAndroidNativeApp()) {
                // Android: plugin nativo MLKit (UI full-screen, não usa <video>)
                await startScannerAndroidNative();
              } else {
                // iOS/web: zxing + getUserMedia + <video> (funciona bem aqui)
                setMode('scan');
                setTimeout(startScanner, 100);
              }
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: 18, borderRadius: 18,
              background: 'linear-gradient(135deg, var(--ember-soft), var(--paper-2))',
              border: '1px solid var(--line)',
              textAlign: 'left',
            }}
          >
            <div style={{
              width: 48, height: 48, borderRadius: 14,
              background: 'var(--ember)', color: 'var(--paper)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon name="camera" size={24} color="var(--paper)" stroke={2}/>
            </div>
            <div style={{ flex: 1 }}>
              <div className="serif" style={{ fontSize: 20, lineHeight: 1.1 }}>Escanear ISBN</div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
                Aponte a câmera para o código de barras do livro.
              </div>
            </div>
            <Icon name="chevron-right" size={16} color="var(--ink-3)"/>
          </button>

          <button
            onClick={() => setMode('manual')}
            style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: 18, borderRadius: 18,
              background: 'var(--paper-2)', border: '1px solid var(--line)',
              textAlign: 'left',
            }}
          >
            <div style={{
              width: 48, height: 48, borderRadius: 14,
              background: 'var(--paper-3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon name="pen" size={22} color="var(--ink-2)" stroke={2}/>
            </div>
            <div style={{ flex: 1 }}>
              <div className="serif" style={{ fontSize: 20, lineHeight: 1.1 }}>Adicionar manualmente</div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
                Digite título, autor e total de páginas.
              </div>
            </div>
            <Icon name="chevron-right" size={16} color="var(--ink-3)"/>
          </button>
          {scanError && (
            <div style={{
              marginTop: 4, padding: '10px 14px', borderRadius: 12,
              background: 'rgba(232,122,59,0.12)', color: 'var(--ember)',
              fontSize: 13, textAlign: 'center',
            }}>{scanError}</div>
          )}
          {scanBusy && (
            <div style={{
              marginTop: 4, padding: '10px 14px', borderRadius: 12,
              background: 'var(--paper-2)', color: 'var(--ink-2)',
              fontSize: 13, textAlign: 'center',
            }}>Preparando câmera…</div>
          )}
        </div>
      )}

      {mode === 'search' && (
        <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 14px', borderRadius: 14, background: 'var(--paper-2)', border: '1px solid var(--line)' }}>
            <Icon name="search" size={18} color="var(--ink-3)"/>
            <input autoFocus value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Título do livro…" style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 15 }} />
            {searchQ && <button onClick={() => { setSearchQ(''); setSearchResults(null); }} style={{ background: 'none', border: 'none' }}><Icon name="close" size={15} color="var(--ink-3)"/></button>}
          </div>
          <div style={{ maxHeight: '46vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {searching && <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>Buscando…</div>}
            {!searching && searchQ.trim().length < 2 && (
              <div style={{ padding: '24px 8px', textAlign: 'center', color: 'var(--ink-4)', fontSize: 13 }}>Digite o nome do livro pra buscar.</div>
            )}
            {!searching && searchErro && searchQ.trim().length >= 2 && (
              <div style={{ padding: '24px 8px', textAlign: 'center', color: 'var(--ink-3)' }}>
                <div style={{ fontSize: 14, color: 'var(--ink-2)', marginBottom: 8 }}>Nao consegui buscar agora.</div>
                <div style={{ fontSize: 12.5, marginBottom: 10 }}>Verifique a conexao e tente de novo.</div>
                <button onClick={() => setSearchQ(searchQ + ' ')} style={{ fontSize: 13, color: 'var(--ember)', background: 'none', border: 'none', fontWeight: 600, cursor: 'pointer' }}>Tentar de novo</button>
              </div>
            )}
            {!searching && !searchErro && searchResults && searchResults.length === 0 && searchQ.trim().length >= 2 && (
              <div style={{ padding: '24px 8px', textAlign: 'center', color: 'var(--ink-3)' }}>
                <div style={{ fontSize: 14, color: 'var(--ink-2)', marginBottom: 8 }}>Não achei esse livro.</div>
                {/* A linha honesta para quem esperava um PDF (CLAREZA §3): a busca
                    sem resultado é onde essa expectativa aparece. */}
                <div data-clareza="sem-pdf" style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--ink-3)', margin: '0 auto 10px', maxWidth: 300 }}>O Forjamente não tem livros pra baixar. Se ainda não tem este, dá pra pegar numa biblioteca pública ou comprar onde preferir — e voltar aqui pra registrar.</div>
                <button onClick={() => { setForm(f => ({ ...f, title: searchQ.trim() })); setMode('manual'); }} style={{ fontSize: 13, color: 'var(--ember)', background: 'none', border: 'none', fontWeight: 600, cursor: 'pointer' }}>Cadastrar manualmente</button>
              </div>
            )}
            {!searching && searchResults && searchResults.map((b, i) => (
              <button key={i} onClick={() => pickSearchResult(b)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 10, borderRadius: 14, background: 'var(--paper-2)', border: '1px solid var(--line)', textAlign: 'left', cursor: 'pointer' }}>
                <ImgCapa src={b.coverSourceUrl || undefined} alt="" style={{ width: 40, height: 58, borderRadius: 6, flexShrink: 0, objectFit: 'cover', background: 'var(--paper-3)' }}
                  fallback={<div style={{ width: 40, height: 58, borderRadius: 6, flexShrink: 0, background: 'var(--paper-3)', display: 'grid', placeItems: 'center' }}><Icon name="book" size={18} color="var(--ink-4)"/></div>}/>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.title}</div>
                  {b.author && <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.author}</div>}
                  {(b.pages || b.publishedDate) && <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>{b.pages ? `${b.pages} págs` : ''}{b.pages && b.publishedDate ? ' · ' : ''}{b.publishedDate ? String(b.publishedDate).slice(0, 4) : ''}</div>}
                </div>
                <Icon name="chevron-right" size={16} color="var(--ink-3)"/>
              </button>
            ))}
          </div>
          <button onClick={() => setMode('choose')} style={{ padding: '12px', borderRadius: 14, background: 'var(--paper-2)', color: 'var(--ink-2)', fontSize: 14, fontWeight: 500, border: '1px solid var(--line)' }}>Voltar</button>
        </div>
      )}

      {mode === 'scan' && (
        <div style={{ marginTop: 18 }}>
          <div style={{
            position: 'relative', aspectRatio: '4 / 3', borderRadius: 18,
            overflow: 'hidden', background: '#000',
          }}>
            <video
              ref={videoRef} autoPlay playsInline muted
              onClick={handleVideoTap}
              onTouchStart={handleVideoTap}
              style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'crosshair' }}
            />
            {/* Overlay guia */}
            <div style={{
              position: 'absolute', inset: '25% 15%',
              border: '2px solid var(--ember)', borderRadius: 12,
              boxShadow: '0 0 0 999px rgba(0,0,0,0.45)',
              pointerEvents: 'none',
            }}/>
            {/* Scan line */}
            <div style={{
              position: 'absolute', left: '15%', right: '15%', top: 'calc(25% + 2px)', height: 2,
              background: 'var(--ember)', animation: 'scan 2.5s ease-in-out infinite',
              pointerEvents: 'none',
            }}/>

            {/* Indicador de toque-para-focar */}
            {focusing && (
              <div style={{
                position: 'absolute',
                left: focusing.x - 28, top: focusing.y - 28,
                width: 56, height: 56, border: '2px solid var(--ember)',
                borderRadius: '50%', pointerEvents: 'none',
                animation: 'focusRing 700ms ease-out forwards',
              }}/>
            )}

            {/* Botão lanterna (se suportado) */}
            {torchSupported && (
              <button onClick={toggleTorch} style={{
                position: 'absolute', top: 12, right: 12,
                width: 44, height: 44, borderRadius: '50%',
                background: torchOn ? 'var(--ember)' : 'rgba(0,0,0,0.55)',
                color: '#fff', backdropFilter: 'blur(6px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid rgba(255,255,255,0.2)',
              }}>
                <Icon name="sun" size={18} color="#fff" stroke={2}/>
              </button>
            )}

            {scanBusy && (
              <div style={{
                position: 'absolute', inset: 0, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 14,
              }}>
                Buscando dados do livro…
              </div>
            )}
          </div>

          <style>{`
            @keyframes focusRing {
              0%   { transform: scale(1.5); opacity: 0; }
              40%  { transform: scale(1);   opacity: 1; }
              100% { transform: scale(0.85); opacity: 0; }
            }
          `}</style>

          <div style={{ marginTop: 14, textAlign: 'center', fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.4 }}>
            Enquadre o código de barras — <span style={{ color: 'var(--ember)' }}>toque na tela para focar</span>.
            {torchSupported && <> Use a <span style={{ color: 'var(--ember)' }}>lanterna</span> se precisar.</>}
          </div>
          {scanError && (
            <div style={{
              marginTop: 12, padding: '10px 14px', borderRadius: 12,
              background: 'rgba(232,122,59,0.12)', color: 'var(--ember)',
              fontSize: 13, textAlign: 'center',
            }}>{scanError}</div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 14 }}>
            <button onClick={() => { stopScanner(); setMode('choose'); }} style={{
              padding: '14px', borderRadius: 14,
              background: 'var(--paper-2)', color: 'var(--ink-2)',
              fontSize: 14, fontWeight: 500, border: '1px solid var(--line)',
            }}>Voltar</button>
            <button onClick={() => { stopScanner(); setMode('manual'); }} style={{
              padding: '14px', borderRadius: 14,
              background: 'var(--ink)', color: 'var(--paper)',
              fontSize: 14, fontWeight: 500,
            }}>Digitar manualmente</button>
          </div>
        </div>
      )}

      {mode === 'preview' && (
        <div style={{ marginTop: 22 }}>
          <div style={{
            display: 'flex', gap: 16, padding: 16, borderRadius: 18,
            background: 'var(--paper-2)', border: '1px solid var(--line)',
          }}>
            <ImgCapa src={form.coverSourceUrl || undefined} alt={form.title} style={{
                width: 72, height: 108, objectFit: 'cover', borderRadius: 6, flexShrink: 0,
                boxShadow: '0 12px 22px -10px rgba(0,0,0,0.35)',
              }}
              fallback={
                <div data-capa-padrao="1" style={{
                  width: 72, height: 108, borderRadius: 6, flexShrink: 0,
                  background: 'linear-gradient(135deg, var(--ember-soft), var(--paper-3))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 6, textAlign: 'center',
                }}><span className="serif" style={{ fontSize: 11, lineHeight: 1.1, color: 'var(--ink-2)' }}>{form.title}</span></div>
              }/>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="serif" style={{ fontSize: 19, lineHeight: 1.15 }}>{form.title}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4, fontStyle: 'italic' }}>
                por {form.author}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                {form.pages && <span className="chip">{form.pages} {unitMeta(form.unit).short}</span>}
                {form.isbn && <span className="chip">ISBN {form.isbn}</span>}
              </div>
            </div>
          </div>

          <div className="field" style={{ marginTop: 14 }}>
            <label>Medir progresso por</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {UNIT_OPTIONS.map(o => (
                <button key={o.value} type="button" onClick={() => setForm({ ...form, unit: o.value })} style={{
                  flex: 1, padding: '10px 6px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  background: form.unit === o.value ? 'var(--ember-soft)' : 'var(--paper-2)',
                  color: form.unit === o.value ? 'var(--ember-2)' : 'var(--ink-3)',
                  border: '1px solid ' + (form.unit === o.value ? 'var(--ember)' : 'var(--line)'),
                }}>{o.label}</button>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="field">
              <label>{unitMeta(form.unit).capital}</label>
              <input type="number" value={form.pages} onChange={e => setForm({ ...form, pages: e.target.value })}/>
            </div>
            <div className="field">
              <label>Meta diária</label>
              <input type="number" value={form.dailyGoal} onChange={e => setForm({ ...form, dailyGoal: e.target.value })}/>
            </div>
          </div>

          {showStatus && <div style={{ marginTop: 14 }}>{statusPicker}</div>}
          {showStatus && <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>{organizeBlock}</div>}

          {error && (
            <div style={{
              marginTop: 12, padding: '10px 14px', borderRadius: 12,
              background: 'rgba(232,122,59,0.12)', color: 'var(--ember)',
              fontSize: 13, textAlign: 'center',
            }}>{error}</div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 18 }}>
            {/* ESCANEAR OUTRO tem de LIGAR a camera, nao so mostrar a tela dela.
                Este botao fazia `setMode('scan')` e nada mais. Os dois efeitos
                que olham `mode === 'scan'` so PARAM o scanner; nenhum liga. A
                pessoa via a moldura, a linha animando e o "toque para focar" —
                sobre uma imagem preta que nunca leria codigo nenhum.

                No Android era pior, e garantido: la o caminho certo e o plugin
                nativo do MLKit, porque zxing + getUserMedia + video nao funciona
                de forma confiavel no WebView — esta escrito no comentario logo
                acima. Entrar em `scan` pelo Android abria uma tela que nunca
                teria como funcionar.

                Agora e o mesmo caminho do botao "Escanear ISBN". */}
            <button onClick={async () => {
              if (isAndroidNativeApp()) { await startScannerAndroidNative(); return; }
              setMode('scan');
              setTimeout(startScanner, 100);
            }} disabled={saving} style={{
              padding: '14px', borderRadius: 14,
              background: 'var(--paper-2)', color: 'var(--ink-2)',
              fontSize: 14, fontWeight: 500, border: '1px solid var(--line)',
            }}>Escanear outro</button>
            <button onClick={handleAdd} disabled={!form.title || !(+form.pages > 0) || saving} style={{
              padding: '14px', borderRadius: 14,
              background: !form.title || !(+form.pages > 0) || saving ? 'var(--paper-3)' : 'var(--ember)',
              color: !form.title || !(+form.pages > 0) || saving ? 'var(--ink-4)' : 'var(--paper)',
              fontSize: 14, fontWeight: 500,
            }}>{saving ? 'Adicionando…' : 'Adicionar livro'}</button>
          </div>
        </div>
      )}

      {mode === 'manual' && (
        <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="field">
            <label>Título</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="Ex: Hábitos Atômicos"/>
          </div>
          <div className="field">
            <label>Autor</label>
            <input value={form.author} onChange={e => setForm({ ...form, author: e.target.value })}
              placeholder="Ex: James Clear"/>
          </div>
          <div className="field">
            <label>Medir progresso por</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {UNIT_OPTIONS.map(o => (
                <button key={o.value} type="button" onClick={() => setForm({ ...form, unit: o.value })} style={{
                  flex: 1, padding: '10px 6px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  background: form.unit === o.value ? 'var(--ember-soft)' : 'var(--paper-2)',
                  color: form.unit === o.value ? 'var(--ember-2)' : 'var(--ink-3)',
                  border: '1px solid ' + (form.unit === o.value ? 'var(--ember)' : 'var(--line)'),
                }}>{o.label}</button>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginTop: 6, lineHeight: 1.35 }}>Páginas (livro físico) · Capítulos · Posições (Kindle/e-book)</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="field">
              <label>{unitMeta(form.unit).capital}</label>
              <input type="number" value={form.pages} onChange={e => setForm({ ...form, pages: e.target.value })} placeholder={form.unit === 'positions' ? '4500' : form.unit === 'chapters' ? '24' : '300'}/>
            </div>
            <div className="field">
              <label>Meta diária</label>
              <input type="number" value={form.dailyGoal} onChange={e => setForm({ ...form, dailyGoal: e.target.value })} placeholder="20"/>
            </div>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginTop: -4, lineHeight: 1.35 }}>Total = quanto o livro tem inteiro (pro progresso). Meta = quanto você quer ler por dia — dá pra mudar depois.</div>
          {showStatus && statusPicker}
          <div className="field">
            <label>Prazo (opcional)</label>
            <input type="date" value={form.targetDate} onChange={e => setForm({ ...form, targetDate: e.target.value })}/>
          </div>
          {showStatus && organizeBlock}

          {error && (
            <div style={{
              padding: '10px 14px', borderRadius: 12,
              background: 'rgba(232,122,59,0.12)', color: 'var(--ember)',
              fontSize: 13, textAlign: 'center',
            }}>{error}</div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8 }}>
            <button onClick={() => setMode('choose')} disabled={saving} style={{
              padding: '14px', borderRadius: 14,
              background: 'var(--paper-2)', color: 'var(--ink-2)',
              fontSize: 14, fontWeight: 500, border: '1px solid var(--line)',
            }}>Voltar</button>
            <button onClick={handleAdd} disabled={!form.title || !(+form.pages > 0) || saving} style={{
              padding: '14px', borderRadius: 14,
              background: !form.title || !(+form.pages > 0) || saving ? 'var(--paper-3)' : 'var(--ember)',
              color: !form.title || !(+form.pages > 0) || saving ? 'var(--ink-4)' : 'var(--paper)',
              fontSize: 14, fontWeight: 500,
            }}>{saving ? 'Adicionando…' : 'Começar a ler'}</button>
          </div>
        </div>
      )}
    </Sheet>
  );
};
